import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loading } from '../../components/ui/loading';
import { ErrorDisplay } from '../../components/ui/error-display';
import { LanguageSwitcher } from '../../shared/ui/language-switcher';

import { useAuthStore } from '../../app/store/auth-store';
import { loginSchema } from '../../shared/lib/validation-schemas';
import { isApiError } from '../../shared/api';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { login, isLoading, isAuthenticated } = useAuthStore();
  
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onSubmit = async (data: any) => {
    try {
      clearErrors();
      await login(data);
      toast.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      if (isApiError(error)) {
        if (error.isUnauthorized) {
          setError('root', {
            type: 'manual',
            message: t('auth.invalidCredentials'),
          });
        } else if (error.isNetworkError) {
          setError('root', {
            type: 'manual',
            message: t('errors.networkError'),
          });
        } else {
          setError('root', {
            type: 'manual',
            message: t('errors.api', { message: error.message }),
          });
        }
      } else {
        setError('root', {
          type: 'manual',
          message: t('errors.unknown'),
        });
      }
    }
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              {t('auth.loginTitle')}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.loginSubtitle')}
            </p>
          </div>

          {/* Language Switcher */}
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="sr-only">
                {t('auth.email')}
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('auth.email')}
                disabled={isFormLoading}
                {...register('email')}
                className={`w-full ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {t(errors.email.message as string)}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="sr-only">
                {t('auth.password')}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder={t('auth.password')}
                disabled={isFormLoading}
                {...register('password')}
                className={`w-full ${errors.password ? 'border-red-500' : ''}`}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {t(errors.password.message as string)}
                </p>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                disabled={isFormLoading}
                {...register('rememberMe')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                {t('auth.rememberMe')}
              </label>
            </div>
          </div>

          {/* Form-level Error Display */}
          {errors.root && (
            <ErrorDisplay 
              error={errors.root.message} 
              className="mt-4"
            />
          )}

          {/* Submit Button */}
          <div>
            <Button
              type="submit"
              disabled={isFormLoading}
              className="w-full"
            >
              {isFormLoading ? (
                <div className="flex items-center">
                  <Loading size="sm" className="mr-2" text="" />
                  {t('common.loading')}
                </div>
              ) : (
                t('auth.login')
              )}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};